import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MetricCard from '../components/MetricCard';
import { adminService } from '../services/adminService';
import { AdminOverview, AdminOverviewAttentionItem, AdminOverviewTrendPoint } from '../types';

const EMPTY_TREND_SERIES: AdminOverviewTrendPoint[] = [];
const EMPTY_ATTENTION_ITEMS: AdminOverviewAttentionItem[] = [];

const formatAmount = (value: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value || 0);

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const formatDayLabel = (value: string): string => {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  }).format(date);
};

const formatAttentionMetric = (item: AdminOverviewAttentionItem): string => {
  if (item.metric_value === null || !item.metric_format) return '-';
  if (item.metric_format === 'currency') return formatAmount(item.metric_value);
  return String(item.metric_value);
};

const getSeriesMax = (
  series: AdminOverviewTrendPoint[],
  accessor: (point: AdminOverviewTrendPoint) => number
): number => {
  const values = series.map(accessor);
  return values.length ? Math.max(...values, 0) : 0;
};

const getSeriesHeight = (value: number, max: number): number => {
  if (max <= 0 || value <= 0) return 8;
  return Math.max(Math.round((value / max) * 100), 12);
};

const truncate = (value: string, max = 72): string =>
  value.length <= max ? value : `${value.slice(0, max - 3)}...`;

const OverviewPage: React.FC = () => {
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const nextOverview = await adminService.getOverview();
      setOverview(nextOverview);
    } catch (nextError: any) {
      setError(nextError.message || 'Erro ao carregar o command center');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadOverview();
  }, [loadOverview]);

  const summary = overview?.summary;
  const dailySeries = overview?.daily_series ?? EMPTY_TREND_SERIES;
  const attentionItems = overview?.attention_items ?? EMPTY_ATTENTION_ITEMS;
  const incidentSnapshot = overview?.incident_snapshot;

  const metricCards = useMemo(
    () =>
      summary
        ? [
            {
              eyebrow: 'Plataforma',
              title: 'Jogadores online',
              value: String(summary.online_players),
              tone: 'emerald' as const,
            },
            {
              eyebrow: 'Operacao',
              title: 'Mesas ativas',
              value: String(summary.active_tables),
              tone: 'blue' as const,
            },
            {
              eyebrow: 'Receita',
              title: 'Rake da plataforma hoje',
              value: formatAmount(summary.platform_rake_today),
              tone: 'amber' as const,
            },
            {
              eyebrow: 'SaaS',
              title: 'MRR estimado',
              value: formatAmount(summary.estimated_mrr),
              tone: 'blue' as const,
            },
            {
              eyebrow: 'Margem',
              title: 'Liquida estimada no mes',
              value: formatAmount(summary.net_platform_revenue_estimate_month),
              tone: 'slate' as const,
            },
            {
              eyebrow: 'Atencao',
              title: 'Itens abertos',
              value: String(summary.open_attention_items),
              tone: 'rose' as const,
            },
            {
              eyebrow: 'Incidents',
              title: 'Criticos abertos',
              value: String(summary.critical_incidents_count),
              tone: 'rose' as const,
            },
          ]
        : [],
    [summary]
  );

  const seriesCards = useMemo(
    () => [
      {
        key: 'platform_rake' as const,
        eyebrow: 'Rake',
        title: 'Plataforma 7d',
        total: formatAmount(dailySeries.reduce((acc, point) => acc + point.platform_rake, 0)),
        tone: 'amber' as const,
        max: getSeriesMax(dailySeries, (point) => point.platform_rake),
        getValue: (point: AdminOverviewTrendPoint) => point.platform_rake,
      },
      {
        key: 'deposit_volume' as const,
        eyebrow: 'Depositos',
        title: 'Entrada 7d',
        total: formatAmount(dailySeries.reduce((acc, point) => acc + point.deposit_volume, 0)),
        tone: 'emerald' as const,
        max: getSeriesMax(dailySeries, (point) => point.deposit_volume),
        getValue: (point: AdminOverviewTrendPoint) => point.deposit_volume,
      },
      {
        key: 'withdrawal_volume' as const,
        eyebrow: 'Saques',
        title: 'Saida 7d',
        total: formatAmount(dailySeries.reduce((acc, point) => acc + point.withdrawal_volume, 0)),
        tone: 'blue' as const,
        max: getSeriesMax(dailySeries, (point) => point.withdrawal_volume),
        getValue: (point: AdminOverviewTrendPoint) => point.withdrawal_volume,
      },
      {
        key: 'gateway_fee' as const,
        eyebrow: 'Gateway',
        title: 'Custo 7d',
        total: formatAmount(dailySeries.reduce((acc, point) => acc + point.gateway_fee, 0)),
        tone: 'rose' as const,
        max: getSeriesMax(dailySeries, (point) => point.gateway_fee),
        getValue: (point: AdminOverviewTrendPoint) => point.gateway_fee,
      },
    ],
    [dailySeries]
  );

  return (
    <div className="admin-page-stack">
      <section className="admin-hero-card">
        <div>
          <p className="admin-section-eyebrow">Command center</p>
          <h1>Visao global da plataforma</h1>
          <p>
            Leitura consolidada de financeiro, clubs, jogadores e incidents operacionais do SaaS.
          </p>
        </div>
        <button type="button" className="admin-secondary-button" onClick={() => void loadOverview()} disabled={loading}>
          {loading ? 'Atualizando...' : 'Atualizar'}
        </button>
      </section>

      {error ? <div className="admin-banner admin-banner--error">{error}</div> : null}

      <section className="admin-metrics-grid">
        {metricCards.map((card) => (
          <MetricCard key={card.title} {...card} />
        ))}
      </section>

      <section className="admin-section-card">
        <div className="admin-section-card__header">
          <div>
            <p className="admin-section-eyebrow">Resumo do periodo</p>
            <h2>KPI financeiro e comercial</h2>
          </div>
          <span className="admin-chip">Gerado em {formatDateTime(overview?.generated_at)}</span>
        </div>

        <div className="admin-kpi-grid">
          <article className="admin-kpi-item">
            <span>Rake plataforma no mes</span>
            <strong>{formatAmount(summary?.platform_rake_month || 0)}</strong>
          </article>
          <article className="admin-kpi-item">
            <span>Depositos no mes</span>
            <strong>{formatAmount(summary?.deposit_volume_month || 0)}</strong>
          </article>
          <article className="admin-kpi-item">
            <span>Saques no mes</span>
            <strong>{formatAmount(summary?.withdrawal_volume_month || 0)}</strong>
          </article>
          <article className="admin-kpi-item">
            <span>Custo gateway no mes</span>
            <strong>{formatAmount(summary?.gateway_fee_month || 0)}</strong>
          </article>
          <article className="admin-kpi-item">
            <span>Owners pagantes</span>
            <strong>{summary?.paying_owner_subscriptions || 0}</strong>
          </article>
          <article className="admin-kpi-item">
            <span>Owners em trial</span>
            <strong>{summary?.trial_owner_subscriptions || 0}</strong>
          </article>
          <article className="admin-kpi-item">
            <span>Owners expirados</span>
            <strong>{summary?.expired_owner_subscriptions || 0}</strong>
          </article>
          <article className="admin-kpi-item">
            <span>Owners suspensos</span>
            <strong>{summary?.suspended_owner_subscriptions || 0}</strong>
          </article>
          <article className="admin-kpi-item">
            <span>Clubs com setup fragil</span>
            <strong>{summary?.clubs_with_payment_issues || 0}</strong>
          </article>
          <article className="admin-kpi-item">
            <span>Falhas de webhook 24h</span>
            <strong>{summary?.failed_payment_events_24h || 0}</strong>
          </article>
          <article className="admin-kpi-item">
            <span>Incidents abertos</span>
            <strong>{summary?.open_incidents_count || 0}</strong>
          </article>
          <article className="admin-kpi-item">
            <span>Incidents criticos</span>
            <strong>{summary?.critical_incidents_count || 0}</strong>
          </article>
          <article className="admin-kpi-item">
            <span>Incidents comerciais</span>
            <strong>{summary?.commercial_incidents_count || 0}</strong>
          </article>
        </div>
      </section>

      <section className="admin-grid admin-grid--two">
        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Incidents</p>
              <h2>Snapshot consolidado do command center</h2>
            </div>
            <span className="admin-chip">{incidentSnapshot?.items.length || 0} item(ns)</span>
          </div>

          <div className="admin-kpi-grid">
            <article className="admin-kpi-item">
              <span>Total no feed</span>
              <strong>{incidentSnapshot?.summary.total_items || 0}</strong>
            </article>
            <article className="admin-kpi-item">
              <span>Abertos</span>
              <strong>{incidentSnapshot?.summary.open_items || 0}</strong>
            </article>
            <article className="admin-kpi-item">
              <span>Criticos</span>
              <strong>{incidentSnapshot?.summary.critical_open || 0}</strong>
            </article>
            <article className="admin-kpi-item">
              <span>Derivados</span>
              <strong>{incidentSnapshot?.summary.derived_items || 0}</strong>
            </article>
          </div>
        </article>

        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Feed</p>
              <h2>Incidents mais relevantes agora</h2>
            </div>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Titulo</th>
                  <th>Categoria</th>
                  <th>Severidade</th>
                  <th>Aberto</th>
                </tr>
              </thead>
              <tbody>
                {incidentSnapshot?.items.length ? (
                  incidentSnapshot.items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <strong>{truncate(item.title)}</strong>
                        <span>{item.source}</span>
                      </td>
                      <td>{item.category}</td>
                      <td>
                        <strong>{item.severity}</strong>
                        <span>{item.status}</span>
                      </td>
                      <td>{formatDateTime(item.opened_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4}>Nenhum incident consolidado esta aberto no snapshot atual.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <section className="admin-grid admin-grid--two">
        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Series</p>
              <h2>Pulso diario do command center</h2>
            </div>
            <span className="admin-chip">Ultimos 7 dias</span>
          </div>

          <div className="admin-series-grid">
            {seriesCards.map((card) => (
              <article key={card.key} className="admin-series-card">
                <p className="admin-section-eyebrow">{card.eyebrow}</p>
                <h3>{card.title}</h3>
                <strong>{card.total}</strong>
                <div className="admin-series-chart">
                  {dailySeries.length ? (
                    dailySeries.map((point) => {
                      const value = card.getValue(point);
                      const height = getSeriesHeight(value, card.max);

                      return (
                        <div key={`${card.key}-${point.date_key}`} className="admin-series-column" title={`${point.date_key}: ${formatAmount(value)}`}>
                          <div
                            className={`admin-series-bar admin-series-bar--${card.tone}`}
                            style={{ height: `${height}%` }}
                          />
                          <span className="admin-series-label">{formatDayLabel(point.date_key)}</span>
                        </div>
                      );
                    })
                  ) : (
                    <div className="admin-note-chip">Sem serie consolidada ainda.</div>
                  )}
                </div>
              </article>
            ))}
          </div>
        </article>

        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Atencao</p>
              <h2>Feed operacional derivado da base real</h2>
            </div>
            <span className="admin-chip">{attentionItems.length} item(ns)</span>
          </div>

          <div className="admin-attention-list">
            {attentionItems.length ? (
              attentionItems.map((item) => (
                <article key={item.id} className={`admin-attention-item admin-attention-item--${item.severity}`}>
                  <div className="admin-attention-item__header">
                    <span className="admin-chip">{item.area}</span>
                    {item.metric_label ? (
                      <span className="admin-attention-item__metric">
                        {item.metric_label}: {formatAttentionMetric(item)}
                      </span>
                    ) : null}
                  </div>
                  <strong>{item.title}</strong>
                  <p>{item.description}</p>
                </article>
              ))
            ) : (
              <div className="admin-note-chip">Nenhuma atencao derivada da base real esta aberta agora.</div>
            )}
          </div>
        </article>
      </section>

      <section className="admin-grid admin-grid--two">
        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Top clubs</p>
              <h2>Melhor desempenho em 30 dias</h2>
            </div>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Club</th>
                  <th>Rake 30d</th>
                  <th>Depositos</th>
                  <th>Saques</th>
                  <th>Setup</th>
                </tr>
              </thead>
              <tbody>
                {overview?.top_clubs?.length ? (
                  overview.top_clubs.map((club) => (
                    <tr key={club.club_id}>
                      <td>
                        <strong>{club.club_name}</strong>
                        <span>{club.club_code}</span>
                      </td>
                      <td>{formatAmount(club.rake_30d)}</td>
                      <td>{formatAmount(club.deposits_30d)}</td>
                      <td>{formatAmount(club.withdrawals_30d)}</td>
                      <td>{club.payment_status || '-'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5}>Nenhum club consolidado ainda.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>

        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Fila de saque</p>
              <h2>Pedidos em revisao na rede</h2>
            </div>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Jogador</th>
                  <th>Club</th>
                  <th>Valor</th>
                  <th>Status</th>
                  <th>Solicitado</th>
                </tr>
              </thead>
              <tbody>
                {overview?.withdrawal_queue?.length ? (
                  overview.withdrawal_queue.map((item) => (
                    <tr key={item.withdrawal_request_id}>
                      <td>{item.user_name}</td>
                      <td>{item.club_name}</td>
                      <td>{formatAmount(item.amount_reais)}</td>
                      <td>{item.status}</td>
                      <td>{formatDateTime(item.requested_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5}>Nenhuma solicitacao em revisao.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <section className="admin-grid admin-grid--two">
        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Assinaturas</p>
              <h2>Owners proximos do vencimento</h2>
            </div>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Owner</th>
                  <th>Plano</th>
                  <th>Status</th>
                  <th>Clubs</th>
                  <th>Expira</th>
                </tr>
              </thead>
              <tbody>
                {overview?.expiring_owners?.length ? (
                  overview.expiring_owners.map((owner) => (
                    <tr key={owner.owner_user_id}>
                      <td>
                        <strong>{owner.owner_name}</strong>
                        <span>{owner.owner_email}</span>
                      </td>
                      <td>{owner.plan_code}</td>
                      <td>{owner.status}</td>
                      <td>{owner.clubs_count}</td>
                      <td>{formatDateTime(owner.expires_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5}>Nenhum owner perto do vencimento.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>

        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Observacoes</p>
              <h2>Frentes ainda em construcao</h2>
            </div>
          </div>

          <div className="admin-note-stack">
            {(overview?.roadmap_notes || []).map((note) => (
              <div key={note} className="admin-note-chip">
                {note}
              </div>
            ))}
          </div>
        </article>
      </section>
    </div>
  );
};

export default OverviewPage;
